/**
 * Hover preview for the block that would be selected on click (world map).
 */

import {
  getLatLngFromBlockNumber,
  integerToBlock,
  pixelIntegerFromLatLng,
} from "./pixel";
import { readLatLngFromMapLocationEvent } from "./mapLocationEvent";
import { screenToLatLngOnMap3d } from "./map3dScreenToLatLng";
import { loadGoogleMaps } from "./googleMapsLoader";
import {
  blockPathForPixel,
  loadPolygonInteractiveLib,
  readMapRange,
  type Map3dElement,
  getSyncedPixelsAllStatus,
  onPixelsAllIndexUpdated,
} from "./worldSoldPixels";

/** Show hover outline when camera range is at most this (meters). */
export const HOVER_MAX_RANGE_M = 25_000;

export type WorldHoverPick = {
  pixel: number;
  lat: number;
  lng: number;
};

let currentPick: WorldHoverPick | null = null;
/** Pick shown when the pointer went down (used on click; cleared after pointerup). */
let pickAtPointerDown: WorldHoverPick | null = null;
let pointerDownClient: { x: number; y: number } | null = null;
let hoverLocked = false;
let refreshHoverFn: (() => void) | null = null;
let cancelPendingFlushFn: (() => void) | null = null;
/** Programmatic preview used by coarse/mobile input where hover does not exist. */
let selectHoverPixelFn: ((pixelInt: number) => WorldHoverPick | null) | null = null;
let clickLockGraceTimer: number | undefined;

const DRAG_THRESHOLD_PX = 6;
/** If no map click follows pointerup, release an accidental lock (ms). */
const CLICK_LOCK_GRACE_MS = 500;

/** Block under the hover outline, or the pick frozen at pointerdown for click. */
export function getWorldHoverPick(): WorldHoverPick | null {
  return pickAtPointerDown ?? currentPick;
}

/**
 * Show and lock the same pixel preview that desktop users get on mouse hover.
 * Used on mobile: first tap previews, second tap on the same pixel confirms.
 */
export function previewWorldHoverPixel(pixelInt: number): WorldHoverPick | null {
  return selectHoverPixelFn?.(pixelInt) ?? null;
}

function clearClickLockGraceTimer(): void {
  if (clickLockGraceTimer != null) {
    window.clearTimeout(clickLockGraceTimer);
    clickLockGraceTimer = undefined;
  }
}

/** Freeze hover outline/pick (popup open or click in progress). */
export function lockWorldHoverPick(): void {
  hoverLocked = true;
  cancelPendingFlushFn?.();
}

/** Call from map click handler — keeps pointerup lock, cancels grace unlock. */
export function confirmWorldHoverClick(): void {
  clearClickLockGraceTimer();
  lockWorldHoverPick();
}

/** Resume hover tracking after the popup closes. */
export function unlockWorldHoverPick(): void {
  clearClickLockGraceTimer();
  hoverLocked = false;
  pickAtPointerDown = null;
  refreshHoverFn?.();
}

function pickFromPixelInt(pixelInt: number): WorldHoverPick {
  const [x, y] = integerToBlock(pixelInt);
  const [lat, lng] = getLatLngFromBlockNumber(x, y);
  return { pixel: pixelInt, lat, lng };
}

export type HoverPixelFlavor = "selectable" | "sold" | "in-cart";

export interface AttachWorldHoverPixelLayerOptions {
  /**
   * Pixels reserved in this visitor's cart: show a distinct “bereits gewählt” cue
   * instead of the unavailable / sold cue from `pixels_all`.
   */
  isPixelInCart?: (pixelId: number) => boolean;
}

const HOVER_MARKER = "data-dwik-hover-pixel";

/** Matches site accent (buttons) — reads as deliberate “Auswahl” framing, not obstruction. */
const HOVER_SELECT_FILL = "rgba(59, 110, 129, 0.26)";
const HOVER_SELECT_STROKE = "rgba(32, 86, 108, 0.93)";
const HOVER_SELECT_STROKE_WIDTH = 5;

const HOVER_SELECT_CROSS_STROKE = "rgba(255, 253, 248, 0.96)";
const HOVER_SELECT_CROSS_WIDTH = 10;

/** User’s basket — greener / warmer than selectable to separate from unavailable. */
const HOVER_INCART_FILL = "rgba(46, 126, 98, 0.3)";
const HOVER_INCART_STROKE = "rgba(16, 86, 64, 0.92)";
const HOVER_INCART_STROKE_WIDTH = 5;
const HOVER_INCART_CROSS_STROKE = "rgba(255, 252, 220, 0.95)";
const HOVER_INCART_CROSS_WIDTH = 9;

/** Aligned with `status !== 0` + sold map overlays — visibly “bereits vergeben”. */
const HOVER_SOLD_FILL = "rgba(178, 65, 55, 0.28)";
const HOVER_SOLD_STROKE = "rgba(132, 35, 28, 0.95)";
const HOVER_SOLD_STROKE_WIDTH = 6;
const HOVER_SOLD_INFO_STROKE = "rgba(255, 251, 245, 0.97)";
const HOVER_SOLD_INFO_RING_WIDTH = 6;
const HOVER_SOLD_INFO_MARK_WIDTH = 8;

const THROTTLE_MS = 32;

function classifyHoverPixelFlavor(
  pixelId: number,
  isPixelInCart?: (id: number) => boolean,
): HoverPixelFlavor {
  if (isPixelInCart?.(pixelId)) return "in-cart";
  const st = getSyncedPixelsAllStatus(pixelId);
  if (st !== undefined && st !== 0) return "sold";
  return "selectable";
}

function altitudeFromCorners(
  corners: google.maps.maps3d.LatLngAltitudeLiteral[],
): number | undefined {
  const a =
    corners[0]?.altitude ??
    corners[1]?.altitude ??
    corners[2]?.altitude ??
    corners[3]?.altitude;
  return typeof a === "number" && Number.isFinite(a) ? a : undefined;
}

/** Center crosshair suggesting “tap to inspect / select this Feld”. */
function selectionCrossPolylines(
  corners: google.maps.maps3d.LatLngAltitudeLiteral[],
): google.maps.maps3d.LatLngAltitudeLiteral[][] {
  if (corners.length < 4) return [];
  const sw = corners[0]!;
  const nw = corners[1]!;
  const ne = corners[2]!;
  const se = corners[3]!;
  const alt = altitudeFromCorners(corners);

  const clat = (sw.lat + nw.lat + ne.lat + se.lat) / 4;
  const clng = (sw.lng + nw.lng + ne.lng + se.lng) / 4;
  const latSpan =
    Math.max(Math.abs(nw.lat - sw.lat), Math.abs(ne.lat - se.lat)) || 1e-7;
  const lngSpan =
    Math.max(Math.abs(ne.lng - nw.lng), Math.abs(se.lng - sw.lng)) || 1e-7;

  const armLat = latSpan * 0.34;
  const armLng = lngSpan * 0.34;

  const z = (): { altitude?: number } => (alt != null ? { altitude: alt } : {});

  return [
    [
      { lat: clat - armLat, lng: clng, ...z() },
      { lat: clat + armLat, lng: clng, ...z() },
    ],
    [
      { lat: clat, lng: clng - armLng, ...z() },
      { lat: clat, lng: clng + armLng, ...z() },
    ],
  ];
}

/** “ℹ” cue — circle + dot + stem (Maps 3D has no icon primitive). */
function soldInfoIconPolylines(
  corners: google.maps.maps3d.LatLngAltitudeLiteral[],
): {
  path: google.maps.maps3d.LatLngAltitudeLiteral[];
  strokeWidth: number;
}[] {
  if (corners.length < 4) return [];
  const sw = corners[0]!;
  const nw = corners[1]!;
  const ne = corners[2]!;
  const se = corners[3]!;
  const alt = altitudeFromCorners(corners);

  const clat = (sw.lat + nw.lat + ne.lat + se.lat) / 4;
  const clng = (sw.lng + nw.lng + ne.lng + se.lng) / 4;
  const latSpan =
    Math.max(Math.abs(nw.lat - sw.lat), Math.abs(ne.lat - se.lat)) || 1e-7;
  const lngSpan =
    Math.max(Math.abs(ne.lng - nw.lng), Math.abs(se.lng - sw.lng)) || 1e-7;

  const ringLat = latSpan * 0.36;
  const ringLng = lngSpan * 0.36;
  const dotLat = latSpan * 0.05;
  const dotLng = lngSpan * 0.05;

  const z = (): { altitude?: number } => (alt != null ? { altitude: alt } : {});
  const at = (dLat: number, dLng: number) => ({
    lat: clat + dLat,
    lng: clng + dLng,
    ...z(),
  });

  const ring: google.maps.maps3d.LatLngAltitudeLiteral[] = [];
  const ringSegs = 22;
  for (let i = 0; i <= ringSegs; i++) {
    const t = (i / ringSegs) * Math.PI * 2;
    ring.push(at(Math.sin(t) * ringLat, Math.cos(t) * ringLng));
  }

  const dotCenterLat = ringLat * 0.4;
  const dot: google.maps.maps3d.LatLngAltitudeLiteral[] = [];
  const dotSegs = 10;
  for (let i = 0; i <= dotSegs; i++) {
    const t = (i / dotSegs) * Math.PI * 2;
    dot.push(
      at(dotCenterLat + Math.sin(t) * dotLat, Math.cos(t) * dotLng),
    );
  }

  const stem: google.maps.maps3d.LatLngAltitudeLiteral[] = [
    at(ringLat * 0.14, 0),
    at(-ringLat * 0.36, 0),
  ];

  return [
    { path: ring, strokeWidth: HOVER_SOLD_INFO_RING_WIDTH },
    { path: dot, strokeWidth: HOVER_SOLD_INFO_MARK_WIDTH },
    { path: stem, strokeWidth: HOVER_SOLD_INFO_MARK_WIDTH },
  ];
}
const DOM_POINTER_EVENTS = ["pointermove", "mousemove"] as const;

function pixelIntegerAtPointer(
  mapEl: Map3dElement,
  evt: PointerEvent,
): number | null {
  if (evt.type.startsWith("gmp-")) {
    const fromApi = readLatLngFromMapLocationEvent(evt);
    if (fromApi) return pixelIntegerFromLatLng(fromApi.lat, fromApi.lng);
  }

  const latLng = screenToLatLngOnMap3d(mapEl, evt.clientX, evt.clientY);
  if (!latLng) return null;
  return pixelIntegerFromLatLng(latLng.lat, latLng.lng);
}

/** Register `gmp-*` pointer events if the loaded maps3d library exposes them. */
async function attachMaps3dPointerEvents(
  mapEl: Map3dElement,
  handler: (evt: Event) => void,
): Promise<string[]> {
  const registered: string[] = [];
  try {
    await loadGoogleMaps();
    const lib = (await google.maps.importLibrary("maps3d")) as unknown as Record<
      string,
      { prototype?: { type?: string } } | undefined
    >;
    for (const key of Object.keys(lib)) {
      if (!/pointer|move|location/i.test(key) || key.includes("Click")) continue;
      const ctor = lib[key];
      const type =
        typeof ctor === "function" &&
        typeof (ctor as { prototype?: { type?: string } }).prototype?.type ===
          "string"
          ? (ctor as { prototype: { type: string } }).prototype.type
          : null;
      if (type && type.startsWith("gmp-")) {
        mapEl.addEventListener(type, handler, { passive: true });
        registered.push(type);
      }
    }
  } catch {
    /* optional enhancement */
  }
  return registered;
}

export function attachWorldHoverPixelLayer(
  mapEl: Map3dElement,
  attachOptions?: AttachWorldHoverPixelLayerOptions,
): () => void {
  const opts = attachOptions ?? {};
  let lastInteger: number | null = null;
  let lastRedrawKey: string | null = null;
  let throttleTimer: number | undefined;
  let pendingPointer: PointerEvent | null = null;
  let lastClient: { x: number; y: number } | null = null;
  let polygonLib: Awaited<ReturnType<typeof loadPolygonInteractiveLib>> = null;
  let gmpPointerTypes: string[] = [];
  let cancelled = false;

  const removeHoverMarkers = () => {
    mapEl.querySelectorAll(`[${HOVER_MARKER}]`).forEach((node) => {
      try {
        mapEl.removeChild(node);
      } catch {
        /* detached */
      }
    });
  };

  const hasHoverMarkers = () => !!mapEl.querySelector(`[${HOVER_MARKER}]`);

  const forceClearHover = () => {
    lastInteger = null;
    lastRedrawKey = null;
    currentPick = null;
    removeHoverMarkers();
  };

  const drawHover = (pixelInt: number) => {
    if (!polygonLib?.PolyCtor) return;

    removeHoverMarkers();

    const flavor = classifyHoverPixelFlavor(pixelInt, opts.isPixelInCart);
    const { PolyCtor, PolylineCtor, altitudeMode } = polygonLib;
    const path = blockPathForPixel(pixelInt, 1);
    const extruded = readMapRange(mapEl) < 30_000;

    let fillColor: string;
    let strokeColor: string;
    let strokeWidth: number;

    switch (flavor) {
      case "in-cart":
        fillColor = HOVER_INCART_FILL;
        strokeColor = HOVER_INCART_STROKE;
        strokeWidth = HOVER_INCART_STROKE_WIDTH;
        break;
      case "sold":
        fillColor = HOVER_SOLD_FILL;
        strokeColor = HOVER_SOLD_STROKE;
        strokeWidth = HOVER_SOLD_STROKE_WIDTH;
        break;
      default:
        fillColor = HOVER_SELECT_FILL;
        strokeColor = HOVER_SELECT_STROKE;
        strokeWidth = HOVER_SELECT_STROKE_WIDTH;
    }

    const gon = new PolyCtor({
      altitudeMode,
      fillColor,
      path,
      strokeColor,
      strokeWidth,
      drawsOccludedSegments: true,
      extruded,
      geodesic: false,
      zIndex: 2,
    });
    gon.setAttribute(HOVER_MARKER, "1");
    gon.setAttribute("integer", String(pixelInt));
    gon.setAttribute("data-dwik-hover-flavor", flavor);

    mapEl.append(gon);

    if (!PolylineCtor) return;

    const corners = path.slice(0, 4);

    if (flavor === "sold") {
      for (const { path: piece, strokeWidth } of soldInfoIconPolylines(corners)) {
        if (piece.length < 2) continue;
        const line = new PolylineCtor({
          altitudeMode,
          path: piece,
          strokeColor: HOVER_SOLD_INFO_STROKE,
          strokeWidth,
          drawsOccludedSegments: true,
          extruded,
          geodesic: false,
          zIndex: 4,
        });
        line.setAttribute(HOVER_MARKER, "1");
        line.setAttribute("integer", String(pixelInt));
        line.setAttribute("data-dwik-hover-accent", "sold-info");
        mapEl.append(line);
      }
      return;
    }

    let accentPieces: google.maps.maps3d.LatLngAltitudeLiteral[][] = [];
    let accentStroke = HOVER_SELECT_CROSS_STROKE;
    let accentWidth = HOVER_SELECT_CROSS_WIDTH;
    const accentZ = 4;

    if (flavor === "selectable") {
      accentPieces = selectionCrossPolylines(corners);
      accentStroke = HOVER_SELECT_CROSS_STROKE;
      accentWidth = HOVER_SELECT_CROSS_WIDTH;
    } else {
      accentPieces = selectionCrossPolylines(corners);
      accentStroke = HOVER_INCART_CROSS_STROKE;
      accentWidth = HOVER_INCART_CROSS_WIDTH;
    }

    for (const piece of accentPieces) {
      if (piece.length < 2) continue;
      const line = new PolylineCtor({
        altitudeMode,
        path: piece,
        strokeColor: accentStroke,
        strokeWidth: accentWidth,
        drawsOccludedSegments: true,
        extruded,
        geodesic: false,
        zIndex: accentZ,
      });
      line.setAttribute(HOVER_MARKER, "1");
      line.setAttribute("integer", String(pixelInt));
      line.setAttribute("data-dwik-hover-accent", flavor);
      mapEl.append(line);
    }
  };

  const applyHoverPixel = (pixelInt: number) => {
    if (hoverLocked) return;
    if (readMapRange(mapEl) > HOVER_MAX_RANGE_M) {
      forceClearHover();
      return;
    }
    const flavor = classifyHoverPixelFlavor(pixelInt, opts.isPixelInCart);
    const redrawKey = `${pixelInt}:${flavor}`;
    if (
      pixelInt === lastInteger &&
      redrawKey === lastRedrawKey &&
      hasHoverMarkers()
    )
      return;
    lastRedrawKey = redrawKey;
    lastInteger = pixelInt;
    currentPick = pickFromPixelInt(pixelInt);
    drawHover(pixelInt);
  };

  selectHoverPixelFn = (pixelInt: number) => {
    if (readMapRange(mapEl) > HOVER_MAX_RANGE_M) {
      forceClearHover();
      return null;
    }

    clearClickLockGraceTimer();
    hoverLocked = false;
    pickAtPointerDown = null;

    const pick = pickFromPixelInt(pixelInt);
    currentPick = pick;
    lastInteger = null;
    lastRedrawKey = null;
    drawHover(pixelInt);

    // Keep the selected preview visible until another tap or popup close.
    hoverLocked = true;
    return pick;
  };

  const cancelPendingFlush = () => {
    if (throttleTimer != null) {
      window.clearTimeout(throttleTimer);
      throttleTimer = undefined;
    }
    pendingPointer = null;
  };

  const flushPointer = () => {
    throttleTimer = undefined;
    const evt = pendingPointer;
    pendingPointer = null;
    if (!evt || cancelled || hoverLocked) return;

    if (readMapRange(mapEl) > HOVER_MAX_RANGE_M) {
      forceClearHover();
      return;
    }

    const pixelInt = pixelIntegerAtPointer(mapEl, evt);
    if (pixelInt == null) return;
    applyHoverPixel(pixelInt);
  };

  const schedulePointer = (evt: PointerEvent) => {
    if (hoverLocked) return;
    lastClient = { x: evt.clientX, y: evt.clientY };
    pendingPointer = evt;
    if (throttleTimer != null) return;
    throttleTimer = window.setTimeout(flushPointer, THROTTLE_MS);
  };

  const refreshHoverAtLastPointer = () => {
    if (hoverLocked) return;
    if (!lastClient || readMapRange(mapEl) > HOVER_MAX_RANGE_M) return;
    const pixelInt = pixelIntegerAtPointer(
      mapEl,
      new PointerEvent("pointermove", {
        clientX: lastClient.x,
        clientY: lastClient.y,
      }),
    );
    if (pixelInt != null) applyHoverPixel(pixelInt);
  };

  refreshHoverFn = refreshHoverAtLastPointer;
  cancelPendingFlushFn = cancelPendingFlush;

  const unsubPixelsAll = onPixelsAllIndexUpdated(() => {
    lastRedrawKey = null;
    refreshHoverAtLastPointer();
  });

  const onMapPointerDown = (evt: PointerEvent) => {
    pickAtPointerDown = currentPick;
    pointerDownClient = { x: evt.clientX, y: evt.clientY };
  };

  const onMapPointerUp = () => {
    pointerDownClient = null;
    if (pickAtPointerDown != null) {
      lockWorldHoverPick();
      clearClickLockGraceTimer();
      clickLockGraceTimer = window.setTimeout(() => {
        clickLockGraceTimer = undefined;
        if (hoverLocked) unlockWorldHoverPick();
      }, CLICK_LOCK_GRACE_MS);
    } else {
      pickAtPointerDown = null;
    }
  };

  const onPointerLike = (evt: Event) => {
    if (hoverLocked) return;
    if (evt instanceof PointerEvent) {
      if (pointerDownClient && evt.buttons !== 0) {
        const dx = evt.clientX - pointerDownClient.x;
        const dy = evt.clientY - pointerDownClient.y;
        if (dx * dx + dy * dy > DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) {
          pickAtPointerDown = null;
        }
      }
      schedulePointer(evt);
      return;
    }
    if (evt instanceof MouseEvent) {
      schedulePointer(
        new PointerEvent("pointermove", {
          clientX: evt.clientX,
          clientY: evt.clientY,
          bubbles: evt.bubbles,
        }),
      );
    }
  };

  const onCameraChange = () => {
    if (readMapRange(mapEl) > HOVER_MAX_RANGE_M) {
      hoverLocked = false;
      forceClearHover();
      return;
    }
    const extruded = readMapRange(mapEl) < 30_000;
    mapEl.querySelectorAll(`[${HOVER_MARKER}]`).forEach((node) => {
      const el = node as HTMLElement;
      if (extruded) el.setAttribute("extruded", "");
      else el.removeAttribute("extruded");
    });
    if (!hoverLocked) refreshHoverAtLastPointer();
  };

  const onPointerLeave = () => {
    if (hoverLocked) return;
    lastClient = null;
    forceClearHover();
  };

  void loadPolygonInteractiveLib().then((lib) => {
    if (cancelled) return;
    polygonLib = lib;
  });

  void attachMaps3dPointerEvents(mapEl, onPointerLike).then((types) => {
    if (!cancelled) gmpPointerTypes = types;
  });

  for (const type of DOM_POINTER_EVENTS) {
    mapEl.addEventListener(type, onPointerLike, { passive: true });
  }
  mapEl.addEventListener("pointerdown", onMapPointerDown, { capture: true });
  mapEl.addEventListener("pointerup", onMapPointerUp, { capture: true });
  mapEl.addEventListener("pointercancel", onMapPointerUp, { capture: true });
  mapEl.addEventListener("pointerleave", onPointerLeave);
  window.addEventListener("gmp-rangechange", onCameraChange, true);
  window.addEventListener("gmp-headingchange", onCameraChange, true);
  window.addEventListener("gmp-tiltchange", onCameraChange, true);
  window.addEventListener("gmp-centerchange", onCameraChange, true);
  window.addEventListener("gmp-fovchange", onCameraChange, true);

  return () => {
    cancelled = true;
    refreshHoverFn = null;
    cancelPendingFlushFn = null;
    selectHoverPixelFn = null;
    hoverLocked = false;
    pickAtPointerDown = null;
    pointerDownClient = null;
    clearClickLockGraceTimer();
    if (throttleTimer != null) window.clearTimeout(throttleTimer);
    for (const type of DOM_POINTER_EVENTS) {
      mapEl.removeEventListener(type, onPointerLike);
    }
    for (const type of gmpPointerTypes) {
      mapEl.removeEventListener(type, onPointerLike);
    }
    mapEl.removeEventListener("pointerdown", onMapPointerDown, { capture: true });
    mapEl.removeEventListener("pointerup", onMapPointerUp, { capture: true });
    mapEl.removeEventListener("pointercancel", onMapPointerUp, { capture: true });
    mapEl.removeEventListener("pointerleave", onPointerLeave);
    window.removeEventListener("gmp-rangechange", onCameraChange, true);
    window.removeEventListener("gmp-headingchange", onCameraChange, true);
    window.removeEventListener("gmp-tiltchange", onCameraChange, true);
    window.removeEventListener("gmp-centerchange", onCameraChange, true);
    window.removeEventListener("gmp-fovchange", onCameraChange, true);
    unsubPixelsAll();
    forceClearHover();
  };
}